import React, { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ExternalChatChannel, ExternalChatMessage, ExternalChatOrganization, ExternalChatUser, } from "domain-entities";
import { Box, makeStyles } from "@material-ui/core";
import { useMessagesStore } from "./MessagesStore";
import ErrorView from "Components/Layout/ErrorView";
import LoadingIndicator from "Components/Layout/LoadingIndicator";
import MessageForm from "./MessageForm";
import { color, px } from "Configs/theme";
import { useUsersOnceStore } from "Components/User/UsersOnceStore";
import Dropzone, { DropzoneState } from "react-dropzone";
import BackupIcon from "@material-ui/icons/Backup";
import GalleryModal from "Components/Gallery/GalleryModal";
import moment from "moment";
import MessageView from "./MessageView";

interface Props {
  organization: ExternalChatOrganization;
  channel: ExternalChatChannel;
  readChannel: () => void;
  viewerUser: ExternalChatUser;
}

const MessagesView: FC<Props> = (props) => {
  const classes = useStyles();
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const lastMessageElementRef = useRef<HTMLDivElement>(null);
  const sendFileRef = useRef<(file: File) => void>();
  const [isHidden, setIsHidden] = useState(true);
  const [galleryModalMessage, setGalleryModalMessage] = useState<ExternalChatMessage>();
  const usersStore = useUsersOnceStore(props.organization.id, [
    ...props.channel.usersActive,
    ...props.channel.usersFormer,
  ]);
  const messagesStore = useMessagesStore(
      props.organization.id,
      props.channel.id
  );
  const previousMessagesLengthRef = useRef(messagesStore.items.length);

  useEffect(() => {
    setTimeout(() => {
      if (messagesStore.items.length > 0) {
        if (previousMessagesLengthRef.current === 0) {
          lastMessageElementRef.current?.scrollIntoView({
            behavior: "auto",
          });

          setTimeout(() => setIsHidden(false));
        } else {
          lastMessageElementRef.current?.scrollIntoView({
            behavior: "smooth",
          });
        }
      }

      previousMessagesLengthRef.current = messagesStore.items.length;
    });
  }, [messagesStore.items.length]);


  const getGroupedLatestMessageEntries = useMemo(() => {
    const messages = messagesStore.items;
    const initValue: { [id: string]: string } = {};
    return Object.entries(messages.reduce((acc, current) => {
      const date = moment(current.timestamp).format('DD MMM YYYY');
      if (acc[date]) {
        return acc;
      }
      acc[date] = current.id
      return acc;
    }, initValue))
  }, [messagesStore.items.length])

  const error = messagesStore.error;
  const isLoading = usersStore.isLoading || messagesStore.isLoading;

  const renderMessages = useMemo(() => {
    // const messages = [...messagesStore.items].slice(
    //   Math.max(messagesStore.items.length - MAX_MESSAGES, 0)
    // );
    const messages = [...messagesStore.items];
    const messagesByDate = getGroupedLatestMessageEntries;

    return messages.map((message) => {
      const senderUser = usersStore.items.find(
          (item) => item.id === message.sender
      );

      const dateHeader = messagesByDate.find(byDate => byDate[1] === message.id);

      return (
          <Fragment>
            {dateHeader && <Box className={classes.stickyDateContainer}>
              <Box className={classes.stickyDate}>
                {dateHeader[0]}
              </Box>
            </Box>}
            <MessageView
                key={message.id}
                message={message}
                viewerUser={props.viewerUser}
                senderUser={senderUser}
                openGalleryModal={() => setGalleryModalMessage(message)}
            />
          </Fragment>);
    });
  }, [messagesStore.items, props.viewerUser, usersStore.items]);

  function renderDropzoneContent(dropzoneState: DropzoneState) {
    return (
      <Box {...dropzoneState.getRootProps()} className={classes.dropzoneRoot}>
        <Box
          className={classes.messages}
          style={{ opacity: isHidden || dropzoneState.isDragActive ? 0 : 1 }}
        >
          <div ref={scrollElementRef} className={classes.scroll}>
            {renderMessages}
            <div ref={lastMessageElementRef} />
          </div>
        </Box>
        <MessageForm
          messagesStore={messagesStore}
          viewerUser={props.viewerUser}
          organization={props.organization}
          channel={props.channel}
          readChannel={props.readChannel}
          sendFileRef={sendFileRef}
        />
        {dropzoneState.isDragActive && (
          <Box className={classes.dropzone}>
            <BackupIcon fontSize="large" />
          </Box>
        )}
      </Box>
    );
  }

  function renderContent() {
    if (error) {
      return <ErrorView error={error} />;
    } else if (isLoading) {
      return <LoadingIndicator />;
    } else {
      return (
        <Dropzone
          noClick
          children={renderDropzoneContent}
          onDrop={(acceptedFiles) =>
            sendFileRef.current && acceptedFiles.forEach(sendFileRef.current)
          }
        />
      );
    }
  }

  return (
    <Fragment>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.channelName}>{props.channel.name}</Box>
          <Box className={classes.organizationName}>
            {props.organization.name}
          </Box>
        </Box>
        {renderContent()}
      </Box>
      <GalleryModal
        message={galleryModalMessage}
        close={() => setGalleryModalMessage(undefined)}
      />
    </Fragment>
  );
};

export default MessagesView;

// const MAX_MESSAGES = 10;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    borderRadius: px.s,
    borderBottomLeftRadius: px.none,
    borderBottomRightRadius: px.none,
    backgroundColor: color.card,
    padding: px.l,
    paddingRight: px.none,
    margin: px.m,
    marginLeft: px.none,
    marginBottom: px.none,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: px.l,
    borderBottom: `1px solid ${color.placeholder}`,
    marginBottom: px.l,
    marginRight: px.l,
  },
  channelName: {
    fontSize: "18px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    marginBottom: px.xs,
  },
  organizationName: {
    fontSize: "14px",
    fontWeight: "bold",
    textTransform: "capitalize",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    opacity: 0.5,
  },
  dropzoneRoot: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    outline: "none",
    flex: 1,
  },
  dropzone: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    marginRight: px.l,
    border: `${px.xs} dashed ${color.primary}`,
    backgroundColor: color.card,
  },
  messages: {
    display: "flex",
    position: "relative",
    flex: 1,
  },
  scroll: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingRight: px.l,
    overflow: "scroll",
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Chrome
    },
  },
  stickyDateContainer: {
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  stickyDate: {
    fontSize: px.m,
    padding: `${px.s} ${px.l}`,
    margin: "auto",
    textAlign: "center",
    backgroundColor: color.primaryBackground01,
    borderRadius: "20px",
    display: "inline-block"
  }
});
